import { List, Map } from 'immutable';
import { createSelector } from 'reselect';

import { selectApplicationLanguage } from 'flow/selectors/app-selectors';
import { selectLoggedInCustomer } from 'flow/selectors/customer-selectors';
import {
    getExtensions,
    selectExtensionDepartment,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';
import { selectExtensionAndContactFavorites } from 'flow/selectors/favorite-selectors';
import {
    createImmutableSelector,
    keepWhileEqual,
} from 'flow/selectors/selector-utils';
import { fuzzySearchColleaguesList } from 'flow/selectors/search-selectors';
import { selectSocialIntegrationSettings } from 'flow/selectors/social-integration-selectors';
import {
    ContactKey,
    ExtensionKey,
    ThirdPartyContactKey,
} from 'flow/types/EntityKeys';
import {
    Contact,
    ExternalContact,
    ThirdPartyContact,
} from 'flow/types/Contact';
import { Extension } from 'flow/types/Extension';
import { Favorite } from 'flow/types/Favorite';
import { State } from 'flow/types/State';
import { TypedMap } from 'flow/types/TypedMap';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';
import {
    extensionSortKey,
    groupExtensionsBySortedDepartment,
    sortContactsByName,
    sortExtensionsByName,
} from 'flow/utils/extension-utils';
import { sortFavorites } from 'flow/utils/favorite-utils';
import { retailerKeysDisallowingConnectedServices } from 'flow/utils/retailer-utils';

type ExtensionSortKey = TypedMap<{
    key: ExtensionKey;
    name: string;
    department: string;
}>;

export type ContactListKey = ExtensionKey | ContactKey | ThirdPartyContactKey;

export type ContactListContactType =
    | 'extension'
    | 'externalContacts'
    | 'socialIntegrationContacts';

export type ContactListContact =
    | TypedMap<Contact>
    | TypedMap<ExternalContact>
    | TypedMap<ThirdPartyContact>;

export interface FuzzyMatch {
    indices: [number, number][];
    value: string;
    key?: string;
}

interface FuzzyContactResult {
    item?: { key: ContactListKey };
    key?: ContactListKey;
    matches?: FuzzyMatch[];
}

export const selectFuzzyContactResults = (
    state: State,
    searchText: string,
): List<FuzzyContactResult> => fuzzySearchColleaguesList(state, searchText);

export const selectExtensionsAreLoading = (state: State) =>
    getExtensions(state).size === 0;

const selectOwnExtensionDepartment = (state: State) =>
    selectExtensionDepartment(state, selectOwnExtensionKey(state));

/* Only the values the list sorts and groups by. Opening a contact card
 * rewrites that extension in the store; while these values stay equal, the
 * list does not sort thousands of extensions again. */
const selectExtensionSortKeys = createImmutableSelector(
    [
        createSelector([getExtensions], (extensions) =>
            extensions
                .filter(
                    (extension: TypedMap<Extension>) =>
                        extension.get('extensionType') !== 'ai_coworker',
                )
                .map(extensionSortKey),
        ),
    ],
    (sortKeys) => sortKeys,
);

// sortStrings reads the app language from localStorage, which can change
// without a reload. The state's language changes together with it, so it is
// an input here: a new language sorts the list again.
const groupExtensionKeysByDepartment = createSelector(
    [
        selectOwnExtensionDepartment,
        selectExtensionSortKeys,
        selectApplicationLanguage,
    ],
    (ownExtensionDepartment, sortKeys) => {
        return groupExtensionsBySortedDepartment(
            sortKeys,
            ownExtensionDepartment,
        )
            .map((departmentList: List<ExtensionSortKey>) =>
                sortExtensionsByName(departmentList),
            )
            .map((departmentList: List<ExtensionSortKey>) =>
                departmentList.map((sortKey) => sortKey.get('key')),
            );
    },
);

/* Opening a contact card rewrites that extension in the store, but the keys
 * and departments usually stay the same, so the list keeps its rows. */
export const selectExtensionKeysByDepartment = createImmutableSelector(
    [groupExtensionKeysByDepartment],
    (extensionKeysByDepartment) => extensionKeysByDepartment,
);

const selectExternalContacts = (state: State) =>
    state.contacts.get('externalContacts');
const selectThirdPartyContacts = (state: State) =>
    state.contacts.get('socialIntegrationContacts');

const contactKeysSortedByName = <Key extends ContactListKey, Entry>(
    contacts: TypedMap<Record<Key, Entry>>,
) => (contacts ? sortContactsByName(contacts).keySeq().toList() : null);

const sortExternalContactKeysByName = createSelector(
    [selectExternalContacts],
    contactKeysSortedByName,
);

const sortThirdPartyContactKeysByName = createSelector(
    [selectThirdPartyContacts],
    contactKeysSortedByName,
);

/* Opening a contact's card rewrites the contacts with the same keys, so the
 * list keeps its rows. */
export const selectExternalContactKeysSortedByName = createImmutableSelector(
    [sortExternalContactKeysByName],
    (contactKeys) => contactKeys,
);

export const selectSocialIntegrationContactKeysSortedByName =
    createImmutableSelector(
        [sortThirdPartyContactKeysByName],
        (contactKeys) => contactKeys,
    );

export const selectFavoriteContactKeys = createSelector(
    [selectExtensionAndContactFavorites],
    (favorites): List<ContactListKey> =>
        sortFavorites(favorites)
            .map((favorite: TypedMap<Favorite>) =>
                favorite.getIn(['favoriteData', 'key']),
            )
            .toList(),
);

export const selectHasSyncedSocialIntegrationAccount = (state: State) => {
    const socialIntegrationSettings = selectSocialIntegrationSettings(state);
    return !!(
        socialIntegrationSettings &&
        (socialIntegrationSettings.has('google') ||
            socialIntegrationSettings.has('microsoft'))
    );
};

export const selectConnectedServicesAreOffered = (state: State) =>
    !(
        retailerKeysDisallowingConnectedServices.includes(
            selectLoggedInCustomer(state).getIn(['retailer', 'key']),
        ) || IS_UAE_HOST
    );

const fuzzyResultKey = (fuzzyItem: FuzzyContactResult) =>
    fuzzyItem?.item?.key ?? fuzzyItem.key;

// Opening a contact's card writes its data again, which runs the search again.
// The sidebar and the number picker can show a contact list at the same time,
// with different search text. Each list makes its own selector, so neither
// replaces the other's result.
export const makeSelectContactSearchResultKeys = () =>
    keepWhileEqual(
        createSelector(
            [selectFuzzyContactResults, selectOwnExtensionKey],
            (fuzzySearchResults, ownExtensionKey): List<ContactListKey> =>
                fuzzySearchResults
                    .map(fuzzyResultKey)
                    .filter((key) => key !== ownExtensionKey)
                    .toList(),
        ),
    );

// Every row asks with the same search text, so one map serves them all.
const selectContactSearchMatchesByKey = createSelector(
    [selectFuzzyContactResults],
    (fuzzySearchResults) =>
        fuzzySearchResults.reduce(
            (matchesByKey, fuzzyItem) =>
                matchesByKey.has(fuzzyResultKey(fuzzyItem))
                    ? matchesByKey
                    : matchesByKey.set(
                          fuzzyResultKey(fuzzyItem),
                          fuzzyItem.matches,
                      ),
            Map<ContactListKey, FuzzyMatch[]>(),
        ),
);

export const selectContactSearchMatches = (
    state: State,
    searchText: string,
    contactListKey: ContactListKey,
) => selectContactSearchMatchesByKey(state, searchText).get(contactListKey);

export const selectContactListContact = (
    state: State,
    entityKey: ContactListKey,
    contactType: ContactListContactType,
): ContactListContact | undefined => {
    switch (contactType) {
        case 'extension':
            return state.extensions
                ?.getIn(['extensions', entityKey as ExtensionKey])
                ?.get('contact', Map());
        case 'externalContacts':
            return state.contacts.getIn([
                'externalContacts',
                entityKey as ContactKey,
            ]);
        case 'socialIntegrationContacts':
            return state.contacts.getIn([
                'socialIntegrationContacts',
                entityKey as ThirdPartyContactKey,
            ]);
        default:
            return undefined;
    }
};
